import { supabase } from '../lib/supabase';
import { Project, ProjectDetailImage, ProjectDetail } from '../types';
import { v4 as uuidv4 } from 'uuid';

// Nom de la table où seront stockés les projets
const PROJECTS_TABLE = 'projects';
const STORAGE_BUCKET = 'agence-interieurs';

/**
 * Convertit un objet Project avec camelCase en format snake_case pour PostgreSQL
 */
const projectToPgFormat = (project: Partial<Project>): Record<string, any> => {
  const pgProject: Record<string, any> = {};
  
  if (project.id) pgProject.id = project.id;
  if (project.title !== undefined) pgProject.title = project.title;
  if (project.location !== undefined) pgProject.location = project.location;
  if (project.description !== undefined) pgProject.description = project.description;
  if (project.keyPoints !== undefined) pgProject.key_points = project.keyPoints;
  if (project.mainImage !== undefined) pgProject.main_image = project.mainImage;
  if (project.images !== undefined) pgProject.images = project.images;
  if (project.year !== undefined) pgProject.year = project.year;
  if (project.category !== undefined) pgProject.category = project.category;
  if (project.subcategory !== undefined) pgProject.subcategory = project.subcategory;
  if (project.isPublished !== undefined) pgProject.is_published = project.isPublished;
  if (project.isFeaturedHome !== undefined) pgProject.is_featured_home = project.isFeaturedHome;
  if (project.position !== undefined) pgProject.position = project.position;
  if (project.galleryPosition !== undefined) pgProject.gallery_position = project.galleryPosition;
  if (project.createdAt) pgProject.created_at = project.createdAt;
  if (project.details) pgProject.details = project.details;
  
  return pgProject;
};

/**
 * Convertit un objet de la base de données avec snake_case en format Project avec camelCase
 */
const pgToProjectFormat = (pgProject: Record<string, any>): Project => {
  return {
    id: pgProject.id,
    title: pgProject.title,
    location: pgProject.location,
    description: pgProject.description,
    keyPoints: pgProject.key_points || [],
    mainImage: pgProject.main_image || '',
    images: pgProject.images || [],
    year: pgProject.year,
    category: pgProject.category,
    subcategory: pgProject.subcategory,
    isPublished: pgProject.is_published,
    isFeaturedHome: pgProject.is_featured_home,
    position: pgProject.position || 1,
    galleryPosition: pgProject.gallery_position || 1,
    createdAt: pgProject.created_at,
    details: pgProject.details || []
  };
};

// Conversion de snake_case à camelCase pour ProjectDetailImage
const snakeToCamelDetailImage = (data: any): ProjectDetailImage => {
  return {
    id: data.id,
    projectId: data.project_id,
    imageUrl: data.image_url,
    position: data.position,
    caption: data.caption,
    createdAt: data.created_at,
  };
};

// Conversion de camelCase à snake_case pour ProjectDetailImage
const camelToSnakeDetailImage = (data: ProjectDetailImage): any => {
  return {
    id: data.id,
    project_id: data.projectId,
    image_url: data.imageUrl,
    position: data.position,
    caption: data.caption,
    created_at: data.createdAt,
  };
};

/**
 * Récupère tous les projets
 */
export const getAllProjects = async (): Promise<Project[]> => {
  const { data, error } = await supabase
    .from(PROJECTS_TABLE)
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Erreur lors de la récupération des projets:', error);
    throw error;
  }

  return (data || []).map(pgToProjectFormat);
};

/**
 * Récupère les projets publiés uniquement, triés par position dans la galerie
 */
export const getPublishedProjects = async (): Promise<Project[]> => {
  const { data, error } = await supabase
    .from(PROJECTS_TABLE)
    .select('*')
    .eq('is_published', true)
    .order('gallery_position', { ascending: true })
    .order('created_at', { ascending: false }); // Tri secondaire par date si même position

  if (error) {
    console.error('Erreur lors de la récupération des projets publiés:', error);
    throw error;
  }

  return (data || []).map(pgToProjectFormat);
};

/**
 * Récupère les projets pour la HomePage (tous les projets publiés)
 * triés par position pour un affichage ordonné
 * Exclut les projets avec position 0 (non affichés sur la page d'accueil)
 */
export const getFeaturedProjects = async (): Promise<Project[]> => {
  // Récupérer les projets publiés triés par position
  const { data, error } = await supabase
    .from(PROJECTS_TABLE)
    .select('*')
    .eq('is_published', true)
    .gt('position', 0) // Exclure les projets avec position 0
    .order('position', { ascending: true }) // Tri par position croissante (1, 2, 3...)
    .limit(9); // Limiter à 9 projets maximum directement dans la requête

  if (error) {
    console.error('Erreur lors de la récupération des projets pour la page d\'accueil:', error);
    throw error;
  }

  // Convertir les données de la base en format Project
  return (data || []).map(pgToProjectFormat);
};

/**
 * Récupère un projet par son ID
 */
export const getProjectById = async (id: string): Promise<Project | null> => {
  const { data, error } = await supabase
    .from(PROJECTS_TABLE)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error(`Erreur lors de la récupération du projet ${id}:`, error);
    throw error;
  }

  return data ? pgToProjectFormat(data) : null;
};

/**
 * Crée un nouveau projet avec une position automatiquement attribuée
 */
export const createProject = async (project: Omit<Project, 'id' | 'createdAt'>): Promise<Project> => {
  // Si aucune position n'est spécifiée, trouver la position maximale actuelle et ajouter 1
  let position = project.position;
  
  if (!position) {
    // Récupérer la position maximale parmi tous les projets
    const { data: maxPositionData, error: maxPositionError } = await supabase
      .from(PROJECTS_TABLE)
      .select('position')
      .order('position', { ascending: false })
      .limit(1);
    
    if (maxPositionError) {
      console.error('Erreur lors de la récupération de la position maximale:', maxPositionError);
      position = 1; // Valeur par défaut en cas d'erreur
    } else {
      // Si des projets existent, prendre la position maximale + 1, sinon commencer à 1
      position = maxPositionData && maxPositionData.length > 0 ? (maxPositionData[0].position || 0) + 1 : 1;
    }
  }
  
  const pgProject = projectToPgFormat({
    ...project,
    id: uuidv4(),
    position: position,
    createdAt: new Date().toISOString()
  });

  const { data, error } = await supabase
    .from(PROJECTS_TABLE)
    .insert([pgProject])
    .select()
    .single();

  if (error) {
    console.error('Erreur lors de la création du projet:', error);
    throw error;
  }

  return pgToProjectFormat(data);
};

/**
 * Met à jour un projet existant
 */
export const updateProject = async (id: string, project: Partial<Project>): Promise<Project> => {
  const pgProject = projectToPgFormat(project);

  const { data, error } = await supabase
    .from(PROJECTS_TABLE)
    .update(pgProject)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error(`Erreur lors de la mise à jour du projet ${id}:`, error);
    throw error;
  }

  return pgToProjectFormat(data);
};

/**
 * Met à jour la position d'un projet
 * Position 0 = Ne pas afficher sur la page d'accueil
 * Position 1-9 = Afficher sur la page d'accueil avec l'ordre spécifié
 */
export const updateProjectPosition = async (id: string, position: number): Promise<Project> => {
  // Vérifier que la position est un nombre valide (0 à 9)
  if (!Number.isInteger(position) || position < 0 || position > 9) {
    throw new Error('La position doit être un nombre entier entre 0 et 9');
  }

  const { data, error } = await supabase
    .from(PROJECTS_TABLE)
    .update({ position })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error(`Erreur lors de la mise à jour de la position du projet ${id}:`, error);
    throw error;
  }

  return pgToProjectFormat(data);
};

/**
 * Supprime un projet
 */
export const deleteProject = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from(PROJECTS_TABLE)
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Erreur lors de la suppression du projet ${id}:`, error);
    throw error;
  }
};

/**
 * Télécharge une image pour un projet
 */
export const uploadProjectImage = async (file: File, projectId: string, projectTitle: string, subfolder?: string): Promise<string> => {
  try {
    if (!file) {
      console.error('Aucun fichier fourni pour l\'upload');
      throw new Error('Aucun fichier fourni');
    }

    console.log('Début de l\'upload de l\'image:', file.name, 'pour le projet:', projectId);
    
    // Formatage du titre du projet pour le nom de fichier
    const formattedTitle = (projectTitle || 'projet')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    
    // Créer un nom de fichier unique basé sur le titre du projet
    const fileExt = file.name.split('.').pop() || 'jpg';
    const fileName = `${formattedTitle}-${Date.now()}.${fileExt}`;
    
    // Créer le chemin du fichier, optionnellement avec un sous-dossier
    const filePath = subfolder
      ? `projects/${projectId}/${subfolder}/${fileName}`
      : `projects/${projectId}/${fileName}`;
    
    console.log('Chemin du fichier pour l\'upload:', filePath);
    
    // Upload du fichier
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('agence-interieurs')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });
    
    if (uploadError) {
      console.error('Erreur Supabase lors de l\'upload:', uploadError);
      throw uploadError;
    }
    
    console.log('Upload réussi:', uploadData);
    
    // Récupérer l'URL publique
    const { data } = supabase.storage
      .from('agence-interieurs')
      .getPublicUrl(filePath);
    
    if (!data || !data.publicUrl) {
      throw new Error('Impossible de récupérer l\'URL publique de l\'image');
    }
    
    console.log('URL publique générée:', data.publicUrl);
    return data.publicUrl;
  } catch (error) {
    console.error('Erreur lors de l\'upload de l\'image:', error);
    throw error;
  }
};

/**
 * Supprime une image de projet
 */
export const deleteProjectImage = async (imageUrl: string): Promise<void> => {
  try {
    // Extraire le chemin de l'image depuis l'URL complète
    // Format attendu: https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/projects/...
    const pathStartIndex = imageUrl.indexOf('/public/') + '/public/'.length;
    
    if (pathStartIndex === -1) {
      console.error('Format d\'URL invalide:', imageUrl);
      return;
    }
    
    const filePath = imageUrl.substring(pathStartIndex);
    
    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
      throw error;
    }
  } catch (err) {
    console.error('Erreur lors de la suppression de l\'image:', err);
    throw err;
  }
};

/**
 * Ajoute une image détaillée
 */
export const addDetailImage = async (projectId: string, file: File, caption?: string): Promise<ProjectDetailImage> => {
  try {
    // 1. Upload de l'image
    const projectData = await getProjectById(projectId);
    if (!projectData) {
      throw new Error(`Projet avec l'ID ${projectId} introuvable`);
    }
    const imageUrl = await uploadProjectImage(file, projectId, projectData.title, 'details');
    
    // 2. Obtenir la position pour la nouvelle image
    const { data: existingImages } = await supabase
      .from('project_detail_images')
      .select('position')
      .eq('project_id', projectId)
      .order('position', { ascending: false })
      .limit(1);
    
    const position = existingImages && existingImages.length > 0 ? existingImages[0].position + 1 : 0;
    
    // 3. Insérer les données dans la table
    const detailImage: ProjectDetailImage = {
      projectId,
      imageUrl,
      position,
      caption: caption || ''
    };
    
    const { data, error } = await supabase
      .from('project_detail_images')
      .insert(camelToSnakeDetailImage(detailImage))
      .select('*')
      .single();
    
    if (error) throw error;
    
    return snakeToCamelDetailImage(data);
  } catch (error) {
    console.error('Erreur lors de l\'ajout de l\'image détaillée:', error);
    throw error;
  }
};

/**
 * Récupère les images détaillées d'un projet
 */
export const getProjectDetailImages = async (projectId: string): Promise<ProjectDetailImage[]> => {
  try {
    const { data, error } = await supabase
      .from('project_detail_images')
      .select('*')
      .eq('project_id', projectId)
      .order('position', { ascending: true });
    
    if (error) throw error;
    
    return data.map(snakeToCamelDetailImage);
  } catch (error) {
    console.error('Erreur lors de la récupération des images détaillées:', error);
    throw error;
  }
};

/**
 * Supprime une image détaillée
 */
export const deleteDetailImage = async (imageId: string): Promise<void> => {
  try {
    // 1. Récupérer les informations de l'image pour pouvoir supprimer le fichier
    const { data: imageData, error: fetchError } = await supabase
      .from('project_detail_images')
      .select('*')
      .eq('id', imageId)
      .single();
    
    if (fetchError) throw fetchError;
    
    // 2. Supprimer l'entrée de la base de données
    const { error: deleteError } = await supabase
      .from('project_detail_images')
      .delete()
      .eq('id', imageId);
    
    if (deleteError) throw deleteError;
    
    // 3. Supprimer le fichier du stockage
    const imageUrl = imageData.image_url;
    const pathParts = imageUrl.split('/');
    const fileName = pathParts[pathParts.length - 1];
    const filePath = `projects/${imageData.project_id}/details/${fileName}`;
    
    const { error: storageError } = await supabase.storage
      .from('agence-interieurs')
      .remove([filePath]);
    
    if (storageError) {
      console.error('Erreur lors de la suppression du fichier de stockage:', storageError);
      // Continuez malgré l'erreur de stockage car l'entrée de BDD est déjà supprimée
    }
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'image détaillée:', error);
    throw error;
  }
};

/**
 * Met à jour la légende d'une image détaillée
 */
export const updateDetailImageCaption = async (imageId: string, caption: string): Promise<ProjectDetailImage> => {
  try {
    const { data, error } = await supabase
      .from('project_detail_images')
      .update({ caption })
      .eq('id', imageId)
      .select('*')
      .single();
    
    if (error) throw error;
    
    return snakeToCamelDetailImage(data);
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la légende:', error);
    throw error;
  }
};

/**
 * Met à jour l'ordre des images détaillées
 */
export const updateDetailImagesOrder = async (images: ProjectDetailImage[]): Promise<void> => {
  try {
    const updates = images.map(image => ({
      id: image.id,
      position: image.position
    }));
    
    for (const update of updates) {
      const { error } = await supabase
        .from('project_detail_images')
        .update({ position: update.position })
        .eq('id', update.id);
      
      if (error) throw error;
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'ordre des images:', error);
    throw error;
  }
};

/**
 * Ajoute un détail à un projet
 */
export const addProjectDetail = async (projectId: string, detail: Omit<ProjectDetail, 'id'>): Promise<ProjectDetail> => {
  try {
    const newDetail = {
      id: uuidv4(),
      projectId,
      ...detail,
      createdAt: new Date().toISOString()
    };
    
    const { error } = await supabase
      .from('project_details')
      .insert([{
        id: newDetail.id,
        project_id: projectId,
        title: newDetail.title,
        value: newDetail.value,
        type: newDetail.type,
        position: newDetail.position,
        created_at: newDetail.createdAt
      }]);
    
    if (error) throw error;
    
    return newDetail as ProjectDetail;
  } catch (error) {
    console.error('Erreur lors de l\'ajout du détail:', error);
    throw error;
  }
};

/**
 * Récupère les détails d'un projet
 */
export const getProjectDetails = async (projectId: string): Promise<ProjectDetail[]> => {
  try {
    const { data, error } = await supabase
      .from('project_details')
      .select('*')
      .eq('project_id', projectId)
      .order('position', { ascending: true });
    
    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      title: item.title,
      value: item.value,
      type: item.type,
      position: item.position
    }));
  } catch (error) {
    console.error('Erreur lors de la récupération des détails du projet:', error);
    return [];
  }
};

/**
 * Supprime un détail de projet
 */
export const deleteProjectDetail = async (detailId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('project_details')
      .delete()
      .eq('id', detailId);
    
    if (error) throw error;
  } catch (error) {
    console.error('Erreur lors de la suppression du détail:', error);
    throw error;
  }
};

/**
 * Met à jour un détail de projet
 */
export const updateProjectDetail = async (detailId: string, detail: Partial<ProjectDetail>): Promise<void> => {
  try {
    const updates: Record<string, any> = {};
    
    if (detail.title !== undefined) updates.title = detail.title;
    if (detail.value !== undefined) updates.value = detail.value;
    if (detail.type !== undefined) updates.type = detail.type;
    if (detail.position !== undefined) updates.position = detail.position;
    
    const { error } = await supabase
      .from('project_details')
      .update(updates)
      .eq('id', detailId);
    
    if (error) throw error;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du détail:', error);
    throw error;
  }
};
